import React, { useLayoutEffect, useRef, useState } from 'react'
import { Floating } from '../component/floating'
import './fold-list.scss'

// A list in a grid cell (a route's alerts, the targets it sends to): as many
// whole items as fit the cell, then a count ("+2 more") that opens all of them.
// Never a word cut in half and never hover-only (owner 2026-11-02: "we need to
// be able to read those"), the same rule as the course cards' platform capsule.
// The count gives way before a word does: where "+2 more" would cut the one
// item left, it reads "+2". A whole set less some (`except`: "All SSO
// alerts" less a platform-only alert) shows the set and "except 1", which
// opens what it leaves out. Only an item too long for the cell even beside
// the shortest count is cut, as a last resort, and its button still opens it
// whole. The count is a real button, so a click on it is its own and never
// opens the row (row-click.js).
export const FoldList = ({ items, except = [], title, separator = ', ', empty = '', className = '' }) => {
  const [open, setOpen] = useState(false)
  const { textRef, shown, short, cut } = useFitItems(items)

  if (!items.length) return <span className={`fold-list ${className}`}><span className="fold-list-text">{empty}</span></span>

  const hidden = items.length - shown
  const text = items.slice(0, shown).join(separator)
  const button = except.length
    ? { label: `except ${except.length}`, name: `${title}: ${items.join(separator)} except ${except.length}`, title: `${items.join(separator)} except`, items: except }
    : { label: hidden > 0 ? (short ? `+${hidden}` : `+${hidden} more`) : 'more', name: `${title}: all ${items.length}`, title, items }
  return (
    <span className={`fold-list ${className}`}>
      <span ref={textRef} className="fold-list-text">{text}</span>
      {(hidden > 0 || cut || except.length > 0) &&
        <Floating placement="bottom-start" isOpen={open} onOpenChange={setOpen} content={<FoldListAll title={button.title} items={button.items} />}>
          <button type="button" className="fold-list-more" aria-haspopup="dialog" aria-expanded={open} aria-label={button.name}>
            {button.label}
          </button>
        </Floating>}
    </span>
  )
}

const FoldListAll = ({ title, items }) => (
  <div className="fold-list-all" role="dialog" aria-label={title}>
    <div className="fold-list-all-title">{title}</div>
    <ul>
      {items.map((item, i) => <li key={i}>{item}</li>)}
    </ul>
  </div>
)

// How many items fit: after each render, while the text is wider than its
// room and more than one item shows, show one fewer; with one left, shorten
// the count; only then is the text cut. A layout effect, so no frame shows it
// cut. When the cell's width changes, or the items do, it starts again from
// all of them.
const useFitItems = items => {
  const textRef = useRef(null)
  const key = items.join('\u0001')
  const all = { key, shown: items.length, short: false, cut: false }
  const [fit, setFit] = useState(all)
  const now = fit.key === key ? fit : all

  useLayoutEffect(() => {
    const el = textRef.current
    if (!el) return
    const over = el.scrollWidth > el.clientWidth
    const hidden = items.length - now.shown
    if (over && now.shown > 1) setFit({ ...now, shown: now.shown - 1 })
    else if (over && hidden > 0 && !now.short) setFit({ ...now, short: true })
    else if (now.shown <= 1 && over !== now.cut) setFit({ ...now, cut: over })
    else if (fit.key !== key) setFit(now)
  }, [key, items.length, fit.key, now])

  useLayoutEffect(() => {
    const cell = textRef.current && textRef.current.parentElement
    if (!cell || typeof ResizeObserver === 'undefined') return undefined
    let width = cell.clientWidth
    const observer = new ResizeObserver(() => {
      if (cell.clientWidth === width) return
      width = cell.clientWidth
      setFit({ key, shown: items.length, short: false, cut: false })
    })
    observer.observe(cell)
    return () => observer.disconnect()
  }, [key, items.length])

  return { textRef, shown: now.shown, short: now.short, cut: now.cut }
}
